// 브랜드키트 관련 Firebase 서비스 함수들
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * 사용자 브랜드키트 조회
 * @param {string} userId - 사용자 ID (provider_id 형식)
 * @returns {Promise<Object>} 브랜드키트 데이터 { colors: [], fonts: [] }
 */
export const fetchUserBrandKit = async (userId) => {
  try {
    if (!userId) {
      throw new Error('userId is required');
    }

    const userDocRef = doc(db, 'USERS', userId);
    const userDocSnap = await getDoc(userDocRef);
    
    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      return userData.brandKit || { colors: [], fonts: [] };
    } else {
      // 문서가 없으면 기본값 반환
      return { colors: [], fonts: [] };
    }
  } catch (error) {
    console.error('브랜드키트 조회 오류:', error);
    throw new Error(`브랜드키트 조회에 실패했습니다: ${error.message}`);
  }
};

/**
 * 사용자 브랜드키트 저장 (활성 프리셋에 저장)
 * @param {string} userId - 사용자 ID (provider_id 형식)
 * @param {Object} brandKit - 브랜드키트 데이터 { colors: [], fonts: [] }
 * @returns {Promise<void>}
 */
export const saveUserBrandKit = async (userId, brandKit) => {
  try {
    if (!userId) {
      throw new Error('userId is required');
    }

    if (!brandKit || typeof brandKit !== 'object') {
      throw new Error('Invalid brandKit data');
    }

    // 유효성 검사
    const validation = validateBrandKit(brandKit);
    if (!validation.isValid) {
      throw new Error('브랜드키트 데이터 유효성 검사 실패: ' + validation.errors.join(', '));
    }

    // 현재 활성 프리셋 조회
    const { activePresetId } = await fetchUserBrandKitPresets(userId);

    // 활성 프리셋에 저장
    await saveUserBrandKitPreset(userId, activePresetId, brandKit);

  } catch (error) {
    console.error('브랜드키트 저장 오류:', error);
    throw new Error(`브랜드키트 저장에 실패했습니다: ${error.message}`);
  }
};

/**
 * 사용자 문서 존재 여부 확인
 * @param {string} userId - 사용자 ID (provider_id 형식)
 * @returns {Promise<boolean>}
 */
export const userDocumentExists = async (userId) => {
  try {
    if (!userId) return false;

    const userDocRef = doc(db, 'USERS', userId);
    const userDocSnap = await getDoc(userDocRef);

    return userDocSnap.exists();
  } catch (error) {
    console.error('사용자 문서 확인 오류:', error);
    return false;
  }
};

/**
 * hex 코드 유효성 검사 및 정규화
 * @param {string} hex - hex 코드 입력값
 * @returns {string|null} 정규화된 hex 코드 또는 null
 */
export const normalizeHexColor = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  
  // # 제거하고 대문자로 변환
  let cleaned = hex.replace('#', '').toUpperCase();
  
  // 3자리 hex를 6자리로 확장 (예: F00 -> FF0000)
  if (cleaned.length === 3) {
    cleaned = cleaned.split('').map(c => c + c).join('');
  }
  
  // 6자리 hex 코드인지 확인
  if (cleaned.length === 6 && /^[A-F0-9]{6}$/.test(cleaned)) {
    return `#${cleaned}`;
  }
  
  return null;
};

/**
 * hex 코드 유효성 검사
 * @param {string} hex - hex 코드 입력값
 * @returns {boolean} 유효한지 여부
 */
export const isValidHexColor = (hex) => {
  return normalizeHexColor(hex) !== null;
};

/**
 * 브랜드키트 데이터 유효성 검사
 * @param {Object} brandKit - 브랜드키트 데이터
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validateBrandKit = (brandKit) => {
  const errors = [];
  
  if (!brandKit || typeof brandKit !== 'object') {
    errors.push('브랜드키트 데이터가 유효하지 않습니다.');
    return { isValid: false, errors };
  }
  
  // 색상 유효성 검사
  if (brandKit.colors) {
    if (!Array.isArray(brandKit.colors)) {
      errors.push('색상 데이터가 배열이 아닙니다.');
    } else {
      if (brandKit.colors.length > 25) {
        errors.push('색상은 최대 25개까지만 허용됩니다.');
      }
      
      brandKit.colors.forEach((color, index) => {
        if (!isValidHexColor(color)) {
          errors.push(`${index + 1}번째 색상이 유효하지 않습니다: ${color}`);
        }
      });
    }
  }
  
  // 폰트 유효성 검사
  if (brandKit.fonts) {
    if (!Array.isArray(brandKit.fonts)) {
      errors.push('폰트 데이터가 배열이 아닙니다.');
    } else {
      if (brandKit.fonts.length > 20) {
        errors.push('폰트는 최대 20개까지만 허용됩니다.');
      }
      
      brandKit.fonts.forEach((font, index) => {
        if (!font || typeof font !== 'string' || font.trim().length === 0) {
          errors.push(`${index + 1}번째 폰트가 유효하지 않습니다.`);
        }
      });
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// =============================================================================
// 다중 프리셋 지원 함수들
// =============================================================================

/**
 * 기존 단일 브랜드키트를 다중 프리셋 구조로 마이그레이션
 * @param {string} userId - 사용자 ID (provider_id 형식)
 * @returns {Promise<void>}
 */
export const migrateBrandKitToPresets = async (userId) => {
  try {
    if (!userId) {
      throw new Error('userId is required');
    }

    const userDocRef = doc(db, 'USERS', userId);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      throw new Error('사용자 문서가 없습니다. 다시 로그인해주세요.');
    }

    const userData = userDocSnap.data();

    // 이미 프리셋 구조인지 확인
    if (userData.brandKitPresets) {
      return;
    }

    // 기존 브랜드키트 데이터 가져오기
    const oldBrandKit = userData.brandKit || { colors: [], fonts: [] };

    // 새로운 프리셋 구조 생성
    const brandKitPresets = {
      preset1: {
        name: '기본',
        colors: oldBrandKit.colors || [],
        fonts: oldBrandKit.fonts || [],
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      preset2: {
        name: '프리셋 2',
        colors: [],
        fonts: [],
        isActive: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      preset3: {
        name: '프리셋 3',
        colors: [],
        fonts: [],
        isActive: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
    };

    // 문서 업데이트 (기존 brandKit 제거하고 새 구조로 변경)
    await updateDoc(userDocRef, {
      brandKitPresets: brandKitPresets,
      activeBrandKitPreset: 'preset1',
      // 기존 brandKit 필드 제거
      brandKit: null
    });

  } catch (error) {
    console.error('브랜드키트 마이그레이션 오류:', error);
    throw new Error(`브랜드키트 마이그레이션에 실패했습니다: ${error.message}`);
  }
};

/**
 * 사용자의 모든 브랜드키트 프리셋 조회
 * @param {string} userId - 사용자 ID (provider_id 형식)
 * @returns {Promise<Object>} { presets: Object, activePresetId: string }
 */
export const fetchUserBrandKitPresets = async (userId) => {
  try {
    if (!userId) {
      throw new Error('userId is required');
    }

    const userDocRef = doc(db, 'USERS', userId);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      // 사용자 문서가 없으면 에러 (Backend에서 OAuth 시 생성됨)
      throw new Error('사용자 문서가 없습니다. 다시 로그인해주세요.');
    }

    const userData = userDocSnap.data();

    // 기존 단일 브랜드키트 구조인지 확인하고 마이그레이션
    if (!userData.brandKitPresets && userData.brandKit) {
      await migrateBrandKitToPresets(userId);
      const migratedUserDocSnap = await getDoc(userDocRef);
      const migratedUserData = migratedUserDocSnap.data();
      return {
        presets: migratedUserData.brandKitPresets || {},
        activePresetId: migratedUserData.activeBrandKitPreset || 'preset1'
      };
    }

    // 프리셋이 없으면 에러 (Backend에서 OAuth 시 생성됨)
    if (!userData.brandKitPresets) {
      throw new Error('브랜드키트 프리셋이 없습니다. 다시 로그인해주세요.');
    }

    return {
      presets: userData.brandKitPresets || {},
      activePresetId: userData.activeBrandKitPreset || 'preset1'
    };
  } catch (error) {
    console.error('브랜드키트 프리셋 조회 오류:', error);
    throw new Error(`브랜드키트 프리셋 조회에 실패했습니다: ${error.message}`);
  }
};

/**
 * 활성 브랜드키트 프리셋 조회 (기존 fetchUserBrandKit과 호환)
 * @param {string} userId - 사용자 ID (provider_id 형식)
 * @returns {Promise<Object>} 활성 브랜드키트 데이터 { colors: [], fonts: [] }
 */
export const fetchActiveBrandKit = async (userId) => {
  try {
    const { presets, activePresetId } = await fetchUserBrandKitPresets(userId);
    const activePreset = presets[activePresetId];
    
    if (!activePreset) {
      return { colors: [], fonts: [] };
    }

    return {
      colors: activePreset.colors || [],
      fonts: activePreset.fonts || []
    };
  } catch (error) {
    console.error('활성 브랜드키트 조회 오류:', error);
    return { colors: [], fonts: [] };
  }
};

/**
 * 특정 프리셋 저장
 * @param {string} userId - 사용자 ID (provider_id 형식)
 * @param {string} presetId - 프리셋 ID (preset1, preset2, preset3)
 * @param {Object} presetData - 프리셋 데이터 { name: string, colors: [], fonts: [] }
 * @returns {Promise<void>}
 */
export const saveUserBrandKitPreset = async (userId, presetId, presetData) => {
  try {
    if (!userId) {
      throw new Error('userId is required');
    }

    if (!presetId || !['preset1', 'preset2', 'preset3'].includes(presetId)) {
      throw new Error('Invalid preset ID. Must be preset1, preset2, or preset3');
    }

    if (!presetData || typeof presetData !== 'object') {
      throw new Error('Invalid preset data');
    }

    // 브랜드키트 유효성 검사
    const validation = validateBrandKit(presetData);
    if (!validation.isValid) {
      throw new Error('프리셋 데이터 유효성 검사 실패: ' + validation.errors.join(', '));
    }

    const userDocRef = doc(db, 'USERS', userId);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      throw new Error('사용자 문서가 없습니다. 다시 로그인해주세요.');
    }

    // 프리셋 업데이트
    const updateData = {};
    updateData[`brandKitPresets.${presetId}.name`] = presetData.name || `프리셋 ${presetId.slice(-1)}`;
    updateData[`brandKitPresets.${presetId}.colors`] = presetData.colors || [];
    updateData[`brandKitPresets.${presetId}.fonts`] = presetData.fonts || [];
    updateData[`brandKitPresets.${presetId}.updatedAt`] = serverTimestamp();

    await updateDoc(userDocRef, updateData);

  } catch (error) {
    console.error('프리셋 저장 오류:', error);
    throw new Error(`프리셋 저장에 실패했습니다: ${error.message}`);
  }
};

/**
 * 활성 프리셋 변경
 * @param {string} userId - 사용자 ID (provider_id 형식)
 * @param {string} presetId - 활성화할 프리셋 ID
 * @returns {Promise<void>}
 */
export const setActiveBrandKitPreset = async (userId, presetId) => {
  try {
    if (!userId) {
      throw new Error('userId is required');
    }

    if (!presetId || !['preset1', 'preset2', 'preset3'].includes(presetId)) {
      throw new Error('Invalid preset ID. Must be preset1, preset2, or preset3');
    }

    const userDocRef = doc(db, 'USERS', userId);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      throw new Error('사용자 문서가 없습니다. 다시 로그인해주세요.');
    }

    // 모든 프리셋의 isActive를 false로 설정하고 선택한 프리셋만 true로 설정
    const updateData = {
      activeBrandKitPreset: presetId,
      'brandKitPresets.preset1.isActive': presetId === 'preset1',
      'brandKitPresets.preset2.isActive': presetId === 'preset2',
      'brandKitPresets.preset3.isActive': presetId === 'preset3'
    };

    await updateDoc(userDocRef, updateData);

  } catch (error) {
    console.error('활성 프리셋 변경 오류:', error);
    throw new Error(`활성 프리셋 변경에 실패했습니다: ${error.message}`);
  }
};

// 참고: brandKitPresets는 Backend OAuth 콜백에서 신규 사용자 등록 시 자동 생성됨
// (functions/auth/google_auth.py, kakao_auth.py, naver_auth.py)